import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE_URL = BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Global 401 handler — when the JWT has expired, dump stale credentials
// and bounce the guest to /login WITH the current URL preserved so we can
// return them right back to where they were (e.g. mid-checkout) after login.
// Without this, axios bubbles up a raw "Token expired" alert with no recovery.
let _redirectingForAuth = false;
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const status = error?.response?.status;
    const detail = String(error?.response?.data?.detail || '').toLowerCase();
    const isAuthFailure = status === 401 && (
      detail.includes('token expired') ||
      detail.includes('invalid token') ||
      detail.includes('not authenticated') ||
      detail.includes('user not found')
    );
    if (isAuthFailure && !_redirectingForAuth) {
      _redirectingForAuth = true;
      try {
        // Drop stale credentials
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Remember where the guest was so we can bring them back after login
        const here = window.location.pathname + window.location.search;
        sessionStorage.setItem('post_login_redirect', here);
        sessionStorage.setItem('session_expired_msg', '1');
      } catch {}
      // Hard-redirect (avoids cross-router issues from an axios interceptor)
      setTimeout(() => { window.location.assign('/login'); }, 50);
      // Suppress the noisy alert chain — the page is about to navigate anyway
      return new Promise(() => {});
    }
    return Promise.reject(error);
  }
);

export default api;

// API endpoints
export const villaAPI = {
  getAll: () => api.get('/villas'),
  getById: (id) => api.get(`/villas/${id}`),
};

export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
};

export const bookingAPI = {
  addToCart: (data) => api.post('/cart/add', data),
  create: (data) => api.post('/bookings/create', data),
  getMyBookings: () => api.get('/bookings/my-bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
};

export const calendarAPI = {
  getAvailability: (villaId, month, year) => 
    api.get(`/calendar/availability/${villaId}`, { params: { month, year } }),
  checkAvailability: (villaId, checkIn, checkOut) =>
    api.post('/calendar/check-availability', { villa_id: villaId, check_in: checkIn, check_out: checkOut }),
};

export const pricingAPI = {
  preview: (data) => api.post('/pricing/preview', data),
};

export const paymentAPI = {
  getPolicy: () => api.get('/payment/policy'),
  createOrder: (data) => api.post('/payments/create-order', data),
  verify: (data) => api.post('/payments/verify', data),
};

export const shootingAPI = {
  getRules: () => api.get('/shooting/rules'),
  book: (data) => api.post('/shooting/book', data),
  getMyBookings: () => api.get('/shooting/bookings'),
};

export const galleryAPI = {
  getPublic: () => api.get('/gallery/public'),
  upload: (data) => api.post('/gallery/upload', data),
};

export const blogAPI = {
  getAll: (limit = 10) => api.get('/blogs', { params: { limit } }),
};

export const contactAPI = {
  getInfo: () => api.get('/contact-info'),
};

export const guestIDAPI = {
  getInfo: () => api.get('/guest-ids/submission-info'),
};

export const upsellAPI = {
  getAll: () => api.get('/upsells'),
};

export const houseRulesAPI = {
  get: () => api.get('/house-rules'),
  accept: (bookingId) => api.post(`/bookings/${bookingId}/accept-rules`),
};

export const indemnityAPI = {
  sign: (data) => api.post('/indemnity/sign', data),
};

export const icalAPI = {
  getLinks: () => api.get('/ical/links'),
  syncAll: () => api.post('/ical/sync'),
  syncVilla: (villaId) => api.post(`/ical/sync/${villaId}`),
  updateLink: (villaId, airbnbUrl) => api.put(`/ical/links/${villaId}`, { airbnb_url: airbnbUrl }),
  clearAirbnbBlocks: (villaId) => api.delete(`/ical/airbnb-blocks/${villaId}`),
  syncStatus: () => api.get('/ical/sync-status'),
};
